// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║   💖 DONATAPP - CUORE PULSANTE ANIMATO                                    ║
// ║   File: src/components/PulsingHeart.jsx                                  ║
// ║                                                                           ║
// ║   Animazione rassicurante per mostrare che l'assistente è "vivo"         ║
// ║   Stati: IDLE, LISTENING, THINKING, SPEAKING                             ║
// ║                                                                           ║
// ║   Ispirato a: ChatGPT voice mode, Google AI, ma con cuore per affetto    ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect } from 'react';

// 🎨 Colori per ogni stato
const stateColors = {
  idle: {
    primary: '#FF6B9D',      // Rosa caldo
    secondary: '#FFB6C1',    // Rosa chiaro
    glow: 'rgba(255,107,157,0.3)',
  },
  listening: {
    primary: '#4CAF50',      // Verde (ascolta)
    secondary: '#81C784',    // Verde chiaro
    glow: 'rgba(76,175,80,0.4)',
  },
  thinking: {
    primary: '#2196F3',      // Blu (pensa)
    secondary: '#64B5F6',    // Blu chiaro
    glow: 'rgba(33,150,243,0.4)',
  },
  speaking: {
    primary: '#FF6B9D',      // Rosa (parla con affetto)
    secondary: '#FFB6C1',
    glow: 'rgba(255,107,157,0.5)',
  },
};

// 🎨 Stili CSS con animazioni
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    position: 'relative',
  },
  
  heartWrapper: {
    position: 'relative',
    width: 200,
    height: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  // Anelli pulsanti attorno al cuore
  pulseRing: {
    position: 'absolute',
    borderRadius: '50%',
    border: '2px solid',
    opacity: 0,
  },
  
  // Stato testuale
  stateLabel: {
    marginTop: 24,
    fontSize: 18,
    fontWeight: 500,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  
  // Messaggio sotto
  message: {
    marginTop: 8,
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    maxWidth: 300,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// 💖 COMPONENTE CUORE SVG ANIMATO
// ═══════════════════════════════════════════════════════════════════════════

const AnimatedHeart = ({ state = 'idle', size = 120 }) => {
  const colors = stateColors[state] || stateColors.idle;
  
  // Velocità animazione per stato
  const animationSpeed = {
    idle: '3s',
    listening: '1.5s',
    thinking: '0.8s',
    speaking: '0.6s',
  };
  
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 100 100"
      style={{
        filter: `drop-shadow(0 0 20px ${colors.glow})`,
        animation: `heartbeat ${animationSpeed[state]} ease-in-out infinite`,
      }}
    >
      <defs>
        {/* Gradiente per effetto 3D */}
        <radialGradient id={`heartGradient-${state}`} cx="30%" cy="30%">
          <stop offset="0%" stopColor={colors.secondary} />
          <stop offset="100%" stopColor={colors.primary} />
        </radialGradient>
        
        {/* Glow effect */}
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Cuore principale */}
      <path
        d="M50 88 C20 60, 5 40, 15 25 C25 10, 45 15, 50 30 C55 15, 75 10, 85 25 C95 40, 80 60, 50 88Z"
        fill={`url(#heartGradient-${state})`}
        filter="url(#glow)"
        style={{
          transformOrigin: 'center',
        }}
      />
      
      {/* Riflesso lucido */}
      <ellipse
        cx="35"
        cy="35"
        rx="8"
        ry="5"
        fill="rgba(255,255,255,0.4)"
        transform="rotate(-30, 35, 35)"
      />
    </svg>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// 🔊 COMPONENTE ONDE SONORE (per stato speaking)
// ═══════════════════════════════════════════════════════════════════════════

const SoundWaves = ({ active, color }) => {
  if (!active) return null;
  
  return (
    <div style={{ 
      position: 'absolute', 
      display: 'flex', 
      alignItems: 'center', 
      gap: 4,
      bottom: -30,
    }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          style={{
            width: 4,
            backgroundColor: color,
            borderRadius: 2,
            animation: `soundWave 0.5s ease-in-out infinite`,
            animationDelay: `${i * 0.1}s`,
          }}
        />
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// 💫 COMPONENTE PRINCIPALE
// ═══════════════════════════════════════════════════════════════════════════

const PulsingHeart = ({ 
  state = 'idle',           // 'idle' | 'listening' | 'thinking' | 'speaking'
  message = '',             // Messaggio opzionale da mostrare
  showLabel = true,         // Mostra etichetta stato
  size = 120,               // Dimensione cuore
  voiceName = 'Assistente', // Nome della voce attiva
}) => {
  const colors = stateColors[state] || stateColors.idle;
  
  // Etichette per ogni stato
  const stateLabels = {
    idle: '💖 Sono qui per te',
    listening: '👂 Ti sto ascoltando...',
    thinking: '💭 Sto pensando...',
    speaking: `🗣️ ${voiceName} sta parlando...`,
  };
  
  // Numero di anelli pulsanti
  const ringCount = state === 'speaking' ? 4 : state === 'listening' ? 3 : 2;
  
  return (
    <div style={styles.container}>
      
      {/* CSS Animations (inline per semplicità) */}
      <style>{`
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(1.1); }
          50% { transform: scale(1); }
          75% { transform: scale(1.05); }
        }
        
        @keyframes heartbeat-fast {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        
        @keyframes pulse-ring {
          0% { 
            transform: scale(0.8); 
            opacity: 0.8; 
          }
          100% { 
            transform: scale(2); 
            opacity: 0; 
          }
        }
        
        @keyframes soundWave {
          0%, 100% { height: 8px; }
          50% { height: 24px; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(255,107,157,0.3); }
          50% { box-shadow: 0 0 40px rgba(255,107,157,0.6); }
        }
      `}</style>
      
      {/* Wrapper con anelli pulsanti */}
      <div style={styles.heartWrapper}>
        
        {/* Anelli che si espandono */}
        {[...Array(ringCount)].map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.pulseRing,
              width: size,
              height: size,
              borderColor: colors.primary,
              animationName: 'pulse-ring',
              animationDuration: `${state === 'speaking' ? 1 : 2}s`,
              animationTimingFunction: 'ease-out',
              animationIterationCount: 'infinite',
              animationDelay: `${i * 0.4}s`,
            }}
          />
        ))}
        
        {/* Cuore animato */}
        <div style={{ 
          animation: state === 'idle' ? 'float 4s ease-in-out infinite' : 'none' 
        }}>
          <AnimatedHeart state={state} size={size} />
        </div>
        
        {/* Onde sonore quando parla */}
        <SoundWaves active={state === 'speaking'} color={colors.primary} />
      </div>
      
      {/* Etichetta stato */}
      {showLabel && (
        <div style={{ ...styles.stateLabel, color: colors.primary }}>
          {stateLabels[state]}
        </div>
      )}
      
      {/* Messaggio opzionale */}
      {message && (
        <div style={styles.message}>
          "{message}"
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// 🎮 COMPONENTE DEMO (per testare le animazioni)
// ═══════════════════════════════════════════════════════════════════════════

export const PulsingHeartDemo = () => {
  const [currentState, setCurrentState] = useState('idle');
  const [voiceName, setVoiceName] = useState('Marco');
  
  const states = ['idle', 'listening', 'thinking', 'speaking'];
  const voices = ['Marco', 'Renzo', 'Dottore', 'Caterina'];
  
  return (
    <div style={{ 
      padding: 40, 
      background: '#F8F9FA', 
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <h2 style={{ textAlign: 'center', marginBottom: 32, color: '#1A1A1A' }}>
        💖 Test Animazione Cuore
      </h2>
      
      {/* Cuore animato */}
      <PulsingHeart 
        state={currentState} 
        voiceName={voiceName}
        message={currentState === 'speaking' ? "Ciao Donata, come stai oggi?" : ""}
      />
      
      {/* Controlli */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: 12, 
        marginTop: 40,
        flexWrap: 'wrap',
      }}>
        {states.map((state) => (
          <button
            key={state}
            onClick={() => setCurrentState(state)}
            style={{
              padding: '12px 24px',
              borderRadius: 12,
              border: 'none',
              background: currentState === state ? '#1976D2' : '#E0E0E0',
              color: currentState === state ? '#fff' : '#333',
              fontWeight: 600,
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {state}
          </button>
        ))}
      </div>
      
      {/* Selezione voce */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: 12, 
        marginTop: 16,
        flexWrap: 'wrap',
      }}>
        {voices.map((voice) => (
          <button
            key={voice}
            onClick={() => setVoiceName(voice)}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: `2px solid ${voiceName === voice ? '#FF6B9D' : '#E0E0E0'}`,
              background: voiceName === voice ? '#FFF0F5' : '#fff',
              color: '#333',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {voice}
          </button>
        ))}
      </div>
      
      {/* Legenda */}
      <div style={{ 
        marginTop: 40, 
        padding: 20, 
        background: '#fff', 
        borderRadius: 16,
        maxWidth: 500,
        margin: '40px auto 0',
      }}>
        <h4 style={{ marginBottom: 12 }}>📋 Stati:</h4>
        <ul style={{ lineHeight: 2, color: '#666' }}>
          <li><strong>idle</strong> - Cuore respira lento, fluttua dolcemente</li>
          <li><strong>listening</strong> - Cuore verde, pulsa attento</li>
          <li><strong>thinking</strong> - Cuore blu, pulsa veloce</li>
          <li><strong>speaking</strong> - Cuore rosa con onde sonore</li>
        </ul>
      </div>
    </div>
  );
};

export default PulsingHeart;
